<script>
import constant from '@constants/constant'
import { broadcastActions, broadcastGetters, authMethods } from '@state/helpers'
import { logDate } from '@utils/logs'

export default {
  page: {
    // All subcomponent titles will be injected into this template.
    titleTemplate(title) {
      title = typeof title === 'function' ? title(this.$store) : title
      return title ? `${title} | ${constant.title}` : constant.title
    },
  },
  data() {
    return {
      info: null,
      remainingSeconds: 60,
      showModalConfirm: false,
      nIntervId: null,
    }
  },
  computed: {
    ...broadcastGetters,
    alertMessage() {
      return `${
        this.remainingSeconds
      } seconds remaining for session expiry. Do you want to continue?`
    },
  },
  methods: {
    ...broadcastActions,
    ...authMethods,
    onConfirm() {
      this.showModalConfirm = false
      this.nIntervId ? clearInterval(this.nIntervId) : clearInterval()
      this.remainingSeconds = 60
      // releasing intervalID
      this.nIntervId = null
      this.$router.push({ name: 'login' })
    },
    onCancel() {
      this.showModalConfirm = false
      this.nIntervId ? clearInterval(this.nIntervId) : clearInterval()
      this.remainingSeconds = 60
      this.getNewToken()

      // let expiryTime = this.$store.state.auth.currentUser.expires_in
      // tokenExpiryTrack(expiryTime)

      // releasing intervalID
      this.nIntervId = null
    },
  },
  onIdle() {
    let FPlusIntegrationID = this.$store.state.fPlusIntegration
      ? this.$store.state.fPlusIntegration.fPlusIntegrationToken || null
      : null
    if (
      window.localStorage.getItem('auth.currentUser') !== 'null' &&
      FPlusIntegrationID === null
    ) {
      const logObj = {
        UserId: this.$store.getters['auth/userId'],
        Message: `User signed out`,
        Description: `User session timed out`,
        LogDateTime: logDate(),
      }
      this.$store.dispatch('sessionLogs/sendLogToAPI', logObj)
      this.nIntervId = setInterval(() => {
        if (this.remainingSeconds === 0) {
          this.showModalConfirm = false
          this.$router.push({
            name: 'login',
            query: { sessionTimedOut: 'true' },
          })
          clearInterval(this.nIntervId)
        } else if (this.remainingSeconds !== 0) {
          this.remainingSeconds -= 1
        }
      }, 1000)

      this.showModalConfirm = true
    }
  },
}
</script>

<template>
  <div id="app">
    <!--
    Even when routes use the same component, treat them
    as distinct and create the component again.
    -->
    {{ info }}
    <RouterView :key="$route.fullPath" />
    <BaseToaster />

    <BaseModalConfirm
      :modal-show="showModalConfirm"
      title-header="Session Timeout"
      :alert-message="alertMessage"
      ok-variant="No"
      cancel-variant="Yes"
      @confirmOkClicked="onConfirm"
      @confirmCancelClicked="onCancel"
    >
    </BaseModalConfirm>
  </div>
</template>

<!-- This should generally be the only global CSS in the app. -->
<style lang="scss">
// Allow element/type selectors, because this is global CSS.
// stylelint-disable selector-max-type, selector-class-pattern

// Normalize default styles across browsers,
// https://necolas.github.io/normalize.css/
@import '~normalize.css/normalize.css';

// https://use.fontawesome.com/releases/v5.0.6/css/all.css
@import 'https://use.fontawesome.com/releases/v5.0.6/css/all.css';

// Design variables and utilities from src/design.
@import '@design';

*,
*::before,
*::after {
  box-sizing: border-box;
}

body {
  background: $color-body-bg;
}

#app {
  @extend %typography-small;
}

// ===
// Base element styles
// ===

a,
a:visited {
  color: $color-link-text;
}

h1 {
  @extend %typography-xxlarge;
}

h2 {
  @extend %typography-xlarge;
}

h3 {
  @extend %typography-large;
}

h4 {
  @extend %typography-medium;
}

h5,
h6 {
  @extend %typography-small;
}

// ===
// Vendor
// ===

// -- Datepicker
input[type='date'] {
  position: relative;
  padding: 0.275rem 0.45rem;
}

/* create a new arrow, because we are going to mess up the native one
see "List of symbols" below if you want another, you could also try to add a font-awesome icon.. */
input[type='date']::after {
  padding: 0 5px;
  font-family: 'Font Awesome 5 Free', Arial, Helvetica, sans-serif;
  font-weight: 900;
  color: #555;
  content: '\f073';
}

/* change color of symbol on hover */
input[type='date']:hover::after {
  /* color: #bf1400; */
}

/* make the native arrow invisible and stretch it over the whole field so you can click anywhere in the input field to trigger the native datepicker */
input[type='date']::-webkit-calendar-picker-indicator {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: auto;
  height: auto;
  color: transparent;
  background: transparent;
}

/* adjust increase/decrease button */
input[type='date']::-webkit-inner-spin-button {
  z-index: 1;
}

/* adjust clear button */
input[type='date']::-webkit-clear-button {
  z-index: 1;
}

.vdpCellContent {
  font-size: 0.8rem !important;
}

// Fix for active tab
.list-group-item.active {
  z-index: 0 !important;
}

// global class for showing disabled cursor
.disabledCursor {
  cursor: not-allowed !important;
}

// Inconsistent arrow fix for dropdown
.multiselect__input,
.multiselect__single {
  vertical-align: baseline !important;
}

.expandGraph {
  position: fixed !important;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 99999;
  width: 100%;
  max-width: 100% !important;
  padding: 0;
  background: #fff;
}
</style>
